// Pagination Model - State management for pagination
import {proxy} from "valtio";

import {EventEmitter} from "@/shared/event-emitter";

interface PaginationEvents {
    change: (page: number, pageSize: number) => void;
    pageSizeChange: (current: number, size: number) => void;
}

interface PaginationConfig {
    current?: number;
    pageSize?: number;
    total?: number;
    defaultPageSize?: number;
    pageSizeOptions?: Array<number>;
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    showTotal?: boolean;
    disabled?: boolean;
}

class PaginationClass {
    // Observable state
    _current: number;
    _pageSize: number;
    _total: number;
    _disabled: boolean;

    // Configuration
    readonly defaultPageSize: number;
    readonly pageSizeOptions: Array<number>;
    readonly showSizeChanger: boolean;
    readonly showQuickJumper: boolean;
    readonly showTotal: boolean;

    // Events
    public readonly events = new EventEmitter<PaginationEvents>();

    constructor(config: PaginationConfig = {}) {
        this._current = config.current || 1;
        this._pageSize = config.pageSize || config.defaultPageSize || 24;
        this._total = config.total || 0;
        this._disabled = config.disabled || false;

        this.defaultPageSize = config.defaultPageSize || 24;
        this.pageSizeOptions = config.pageSizeOptions || [10, 20, 50, 100];
        this.showSizeChanger = config.showSizeChanger !== false; // default true
        this.showQuickJumper = config.showQuickJumper !== false; // default true
        this.showTotal = config.showTotal !== false; // default true
    }

    // Getters
    get current(): number {
        return this._current;
    }

    get pageSize(): number {
        return this._pageSize;
    }

    get total(): number {
        return this._total;
    }

    get disabled(): boolean {
        return this._disabled;
    }

    get totalPages(): number {
        return Math.ceil(this._total / this._pageSize);
    }

    get hasNext(): boolean {
        return this._current < this.totalPages;
    }

    get hasPrev(): boolean {
        return this._current > 1;
    }

    get startIndex(): number {
        return (this._current - 1) * this._pageSize;
    }

    get endIndex(): number {
        return Math.min(this._current * this._pageSize, this._total);
    }

    // Actions
    setPage(page: number, silent = false): void {
        if (page < 1 || page > this.totalPages || this._disabled) return;

        this._current = page;
        if (!silent) {
            this.events.emit("change", this._current, this._pageSize);
        }
    }

    setPageSize(size: number): void {
        if (!this.pageSizeOptions.includes(size) || this._disabled) return;

        const oldPageSize = this._pageSize;
        this._pageSize = size;

        // Adjust current page to maintain approximate position
        const newPage = Math.ceil((this._current * oldPageSize) / size);
        this._current = Math.max(1, Math.min(newPage, this.totalPages));

        this.events.emit("pageSizeChange", this._current, this._pageSize);
        this.events.emit("change", this._current, this._pageSize);
    }

    setTotal(total: number, silent = false): void {
        this._total = total;

        // If current page is now out of bounds, adjust it
        if (this._current > this.totalPages) {
            this._current = Math.max(1, this.totalPages);
            if (!silent) this.events.emit("change", this._current, this._pageSize);
        }
    }

    nextPage(): void {
        if (this.hasNext) {
            this.setPage(this._current + 1);
        }
    }

    prevPage(): void {
        if (this.hasPrev) {
            this.setPage(this._current - 1);
        }
    }

    firstPage(): void {
        this.setPage(1);
    }

    lastPage(): void {
        this.setPage(this.totalPages);
    }

    reset(): void {
        this._current = 1;
        this._pageSize = this.defaultPageSize;
        this.events.emit("change", this._current, this._pageSize);
    }

    setDisabled(disabled: boolean): void {
        this._disabled = disabled;
    }

    // Query params support
    getQueryParam(): string {
        if (this._current > 1) {
            return `page=${this._current}`;
        }
        return "";
    }

    setFromQueryParams(params: URLSearchParams): void {
        const page = params.get("page") ?? 1;
        if (page) {
            const pageNum = Number(page);
            if (!isNaN(pageNum) && pageNum > 0) {
                this._current = pageNum;
            }
        }
    }
}

// Export type
export type PaginationInstance = PaginationClass;

// Factory function
export function Pagination(config: PaginationConfig = {}): PaginationInstance {
    return proxy<PaginationInstance>(new PaginationClass(config));
}
